/**
 * The Inbox's data — `docs/activity.md` §7.1: the conversations of whoever
 * the people button picks (`GET /conversations`, 40 at a time, pinned then
 * newest first — §7.10), and the open one's thread (`GET /conversations/:id`).
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import type {
  ConversationList, ConversationRow, ConversationThread, InboxCategory, InboxFilter,
} from '@rowted/shared'
import { activityRequest, peopleQuery } from '../api.js'
import { errorWords } from './look.js'

export interface ListQuery {
  people: readonly string[]
  unassigned: readonly string[]
  filter: InboxFilter
  channel: 'all' | 'sms' | 'email'
  /** One of the viewer's categories (§7.10). */
  category: string | null
  q: string
}

export interface ConversationsHandle {
  rows: ConversationRow[]
  counts: { all: number; unread: number } | null
  /** The viewer's categories (§7.10), as the list last said them. */
  categories: InboxCategory[]
  timeZone: string | null
  loading: boolean
  loadingMore: boolean
  hasMore: boolean
  error: string | null
  loadMore: () => void
  /** The first page again, quietly — after a send, a take, a minute going by. */
  reload: () => void
  /** Changes one row on screen (read on leaving it). */
  patch: (id: string, change: Partial<ConversationRow>) => void
  /** Takes a row off the screen — archived, or out of the archive (§7.10). */
  drop: (id: string) => void
  setCounts: (counts: { all: number; unread: number }) => void
  setCategories: (categories: InboxCategory[]) => void
}

function listPath(query: ListQuery, cursor: string | null): string {
  const params = new URLSearchParams()
  params.set('people', peopleQuery(query.people))
  params.set('unassigned', peopleQuery(query.unassigned))
  params.set('filter', query.filter)
  params.set('channel', query.channel)
  if (query.category) params.set('category', query.category)
  if (query.q.trim()) params.set('q', query.q.trim())
  if (cursor) params.set('cursor', cursor)
  return `/conversations?${params.toString()}`
}

export function useConversations(query: ListQuery): ConversationsHandle {
  const [rows, setRows] = useState<ConversationRow[]>([])
  const [counts, setCounts] = useState<{ all: number; unread: number } | null>(null)
  const [categories, setCategories] = useState<InboxCategory[]>([])
  const [timeZone, setTimeZone] = useState<string | null>(null)
  const [cursor, setCursor] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [tick, setTick] = useState(0)
  const asked = useRef(0)

  const key = listPath(query, null)
  /* A new question starts from the top; a reload of the same one keeps the rows on screen meanwhile. */
  const lastKey = useRef<string | null>(null)

  useEffect(() => {
    const ask = ++asked.current
    const fresh = lastKey.current !== key
    lastKey.current = key
    if (fresh) setLoading(true)
    activityRequest<ConversationList>(key)
      .then((answer) => {
        if (ask !== asked.current) return
        setRows((current) => {
          if (fresh) return answer.items
          /* A quiet reload keeps the pages already loaded below the first. */
          const firstIds = new Set(answer.items.map((row) => row.id))
          const rest = current.slice(answer.items.length).filter((row) => !firstIds.has(row.id))
          return [...answer.items, ...rest]
        })
        if (fresh) setCursor(answer.nextCursor)
        setCounts(answer.counts)
        setCategories(answer.categories ?? [])
        setTimeZone(answer.timeZone)
        setError(null)
      })
      .catch((cause: unknown) => { if (ask === asked.current) setError(errorWords(cause)) })
      .finally(() => { if (ask === asked.current) setLoading(false) })
  }, [key, tick])

  const loadMore = useCallback(() => {
    if (!cursor || loadingMore) return
    setLoadingMore(true)
    const ask = asked.current
    activityRequest<ConversationList>(listPath(query, cursor))
      .then((answer) => {
        if (ask !== asked.current) return
        setRows((current) => {
          const seen = new Set(current.map((row) => row.id))
          return [...current, ...answer.items.filter((row) => !seen.has(row.id))]
        })
        setCursor(answer.nextCursor)
      })
      .catch((cause: unknown) => setError(errorWords(cause)))
      .finally(() => setLoadingMore(false))
  }, [cursor, loadingMore, query])

  const reload = useCallback(() => setTick((n) => n + 1), [])
  const patch = useCallback((id: string, change: Partial<ConversationRow>) => {
    setRows((current) => current.map((row) => (row.id === id ? { ...row, ...change } : row)))
  }, [])
  const drop = useCallback((id: string) => {
    setRows((current) => current.filter((row) => row.id !== id))
  }, [])

  return {
    rows, counts, categories, timeZone, loading, loadingMore, hasMore: cursor !== null, error, loadMore, reload, patch,
    drop, setCounts, setCategories,
  }
}

export interface ThreadHandle {
  thread: ConversationThread | null
  loading: boolean
  error: string | null
  reload: () => void
}

/** One thread. Reading it is recorded by the tab (§7.1) — a thread the page opened by itself is not read yet. */
export function useThread(id: string | null): ThreadHandle {
  const [thread, setThread] = useState<ConversationThread | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [tick, setTick] = useState(0)
  const shownId = useRef<string | null>(null)

  useEffect(() => {
    if (!id) {
      setThread(null)
      shownId.current = null
      return
    }
    let live = true
    if (shownId.current !== id) {
      setLoading(true)
      setThread(null)
    }
    activityRequest<ConversationThread>(`/conversations/${id}`)
      .then((answer) => {
        if (!live) return
        setThread(answer)
        setError(null)
        shownId.current = id
      })
      .catch((cause: unknown) => { if (live) setError(errorWords(cause)) })
      .finally(() => { if (live) setLoading(false) })
    return () => { live = false }
  }, [id, tick])

  const reload = useCallback(() => setTick((n) => n + 1), [])
  return { thread, loading, error, reload }
}
